import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearch } from '@tanstack/react-router';
import type { ReportResponse } from '@quire/shared';
import { api } from '../lib/api';
import { Empty, PageHeading, Spinner } from '../components/ui';
import { Ledger, LedgerToolbar, searchRows, type LedgerColumn } from '../components/Ledger';
import { useListView } from '../lib/useListView';

type ReportRow = ReportResponse['rows'][number];

type GroupBy = 'student' | 'class' | 'period' | 'date';

const GROUPS: { value: GroupBy; label: string }[] = [
  { value: 'student', label: 'Student' },
  { value: 'class', label: 'Class' },
  { value: 'period', label: 'Period' },
  { value: 'date', label: 'Date' },
];

function monthsAgo(count: number): string {
  const date = new Date();
  date.setUTCMonth(date.getUTCMonth() - count);
  return date.toISOString().slice(0, 10);
}

/*
 * A report is not grouped on the client. It already chooses an aggregation
 * dimension on the server, which decides which rows exist at all and what the
 * first column means, so a second thing called grouping would be two different
 * ideas sharing one word.
 */
function columnsFor(label: string, totals: ReportResponse['totals'] | undefined) {
  const figure = (
    id: string,
    header: string,
    of: (row: ReportRow) => number,
    total: number | undefined,
    className: string,
  ): LedgerColumn<ReportRow> => ({
    id,
    header,
    ruled: true,
    align: 'right',
    numeric: true,
    sort: of,
    className: `font-mono tabular ${className}`,
    cell: of,
    foot: <span className={className.includes('ink-soft') ? '' : 'font-semibold'}>{total}</span>,
  });

  const columns: LedgerColumn<ReportRow>[] = [
    {
      id: 'label',
      header: label,
      sort: (row) => row.label,
      search: (row) => row.label,
      className: 'font-medium',
      cell: (row) => row.label,
      foot: <span className="eyebrow">All</span>,
    },
    figure('present', 'Present', (row) => row.present, totals?.present, ''),
    figure('late', 'Late', (row) => row.late, totals?.late, ''),
    figure('absent', 'Absent', (row) => row.absent, totals?.absent, ''),
    figure('excused', 'Excused', (row) => row.excused, totals?.excused, 'text-ink-soft'),
    {
      id: 'rate',
      header: 'Rate',
      ruled: true,
      align: 'right',
      numeric: true,
      /*
       * A null rate is "nobody was expected", which is a different fact from a
       * rate of zero, and the comparator keeps it out of the running rather
       * than filing it at the bottom of the scale.
       */
      sort: (row) => row.rate,
      cell: (row) => <RateBar rate={row.rate} />,
      foot: <RateBar rate={totals?.rate ?? null} />,
    },
  ];
  return columns;
}

export function Reports() {
  const search = useSearch({ from: '/app/reports' });
  const from = search.from ?? monthsAgo(3);
  const to = search.to ?? new Date().toISOString().slice(0, 10);
  const groupBy: GroupBy = search.by ?? 'student';

  const report = useQuery({
    queryKey: ['report', from, to, groupBy],
    queryFn: () => api.reports.attendance({ from, to, groupBy }),
  });

  const totals = report.data?.totals;
  const columns = useMemo(
    () => columnsFor(GROUPS.find((g) => g.value === groupBy)?.label ?? 'Group', totals),
    [groupBy, totals],
  );
  const view = useListView({ search, columns });

  const all = useMemo(() => report.data?.rows ?? [], [report.data]);
  const rows = useMemo(() => searchRows(all, columns, view.query), [all, columns, view.query]);

  return (
    <div>
      <PageHeading eyebrow="Attendance over time" title="Reports">
        <div className="flex flex-wrap items-end gap-2">
          <label className="grid gap-1">
            <span className="eyebrow">From</span>
            <input
              type="date"
              className="field w-auto font-mono"
              value={from}
              onChange={(e) => view.set({ from: e.target.value })}
            />
          </label>
          <label className="grid gap-1">
            <span className="eyebrow">To</span>
            <input
              type="date"
              className="field w-auto font-mono"
              value={to}
              onChange={(e) => view.set({ to: e.target.value })}
            />
          </label>
          <label className="grid gap-1">
            <span className="eyebrow">Group by</span>
            <select
              className="field w-auto"
              value={groupBy}
              onChange={(e) => view.set({ by: e.target.value })}
            >
              {GROUPS.map((group) => (
                <option key={group.value} value={group.value}>
                  {group.label}
                </option>
              ))}
            </select>
          </label>
        </div>
      </PageHeading>

      {/*
        The rule is stated in the interface, not buried in a tooltip: a number
        whose definition is invisible is a number people quietly mistrust.
      */}
      <p className="mb-4 text-xs text-ink-soft">
        Rate counts present and late as attended. Excused absences are left out of the calculation
        entirely, so they neither raise nor lower the figure.
      </p>

      {report.isLoading ? (
        <Spinner label="Calculating" />
      ) : (
        <>
          <LedgerToolbar
            query={view.draft}
            onQuery={view.onQuery}
            searchPlaceholder="name in the first column"
            shown={rows.length}
            total={all.length}
          />
          <Ledger
            caption="Attendance"
            rows={rows}
            total={all.length}
            columns={columns}
            rowId={(row) => row.key}
            sort={view.sort}
            onSort={view.onSort}
            foot={Boolean(totals)}
            empty={
              <Empty
                title="Nothing marked in this range"
                hint="Widen the dates, or take attendance first - reports are built from saved marks."
              />
            }
            noMatch={<Empty title="No match" hint="No row in this report has that name." />}
          />
        </>
      )}
    </div>
  );
}

/**
 * A rate with no countable marks is shown as "-", never as 0%. "Nobody was
 * expected" and "nobody came" are different facts and a report that renders
 * them identically is simply wrong.
 */
function RateBar({ rate }: { rate: number | null }) {
  if (rate === null) {
    return (
      <span className="font-mono text-ink-faint" title="Every mark was excused">
        -
      </span>
    );
  }
  const percent = Math.round(rate * 100);
  return (
    <span className="inline-flex items-center justify-end gap-2">
      <span aria-hidden className="h-1.5 w-16 overflow-hidden rounded-full bg-paper-sunk">
        <span
          className="block h-full rounded-full"
          style={{
            width: `${percent}%`,
            background:
              percent >= 90
                ? 'var(--color-present)'
                : percent >= 75
                  ? 'var(--color-late)'
                  : 'var(--color-absent)',
          }}
        />
      </span>
      <span className="w-10 font-mono font-semibold tabular">{percent}%</span>
    </span>
  );
}
